import { useState, useLayoutEffect, useCallback } from "react";

const DEFAULT_BOTTOM_GAP = 12;
const CONTENT_ZOOM_FALLBACK = 0.75;

function getContentZoom(node) {
  let el = node;
  while (el) {
    if (el.classList?.contains("content")) {
      const zoom = parseFloat(window.getComputedStyle(el).zoom);
      return Number.isFinite(zoom) && zoom > 0 ? zoom : CONTENT_ZOOM_FALLBACK;
    }
    el = el.parentElement;
  }
  return CONTENT_ZOOM_FALLBACK;
}

/**
 * Max CSS height for content inside `.content { zoom: 0.75 }` that should
 * visually reach the bottom of the viewport without extending past it.
 */
export function useViewportBoundedHeight(anchorEl, bottomGap = DEFAULT_BOTTOM_GAP) {
  const [maxHeight, setMaxHeight] = useState(null);

  const measure = useCallback(() => {
    if (!anchorEl || typeof window === "undefined") return;

    const top = anchorEl.getBoundingClientRect().top;
    const availableViewportPx = window.innerHeight - top - bottomGap;
    if (availableViewportPx <= 0) return;

    const zoom = getContentZoom(anchorEl);
    const availableCssPx = availableViewportPx / zoom;

    setMaxHeight(Math.max(200, Math.floor(availableCssPx)));
  }, [anchorEl, bottomGap]);

  useLayoutEffect(() => {
    if (!anchorEl) return;

    measure();

    const rafId = requestAnimationFrame(measure);
    const timeoutId = window.setTimeout(measure, 100);
    const timeoutId2 = window.setTimeout(measure, 400);

    window.addEventListener("resize", measure);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;

    if (ro) {
      let el = anchorEl;
      while (el) {
        ro.observe(el);
        if (el.classList?.contains("content")) break;
        el = el.parentElement;
      }
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(timeoutId2);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [measure, anchorEl]);

  return maxHeight;
}

export const V2_POSITION_PANEL_HEADER_HEIGHT = 44;
